type RawPair = [unknown, unknown] | unknown;

const parseNumber = (value: unknown): number => {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.length === 0) return 0;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

export const ensurePair = (value: RawPair): [number, number] => {
  if (Array.isArray(value) && value.length >= 2) {
    return [parseNumber(value[0]), parseNumber(value[1])];
  }

  if (typeof value === "string") {
    if (value.includes(":")) {
      const [home, away] = value.split(":");
      return [parseNumber(home), parseNumber(away)];
    }
    if (value.includes(",")) {
      const [home, away] = value.split(",");
      return [parseNumber(home), parseNumber(away)];
    }
  }

  return [0, 0];
};

export const formatScoreCell = (value: number): string => {
  if (value === null || value === undefined) return "-";
  if (!Number.isFinite(value)) return "-";
  if (value < 0) return "-";
  return String(value);
};

export type InningScore = {
  key: string;
  order: number;
  label: string;
  scores: [number, number];
};

export const collectInningScores = (
  c: Record<string, unknown> | undefined
): InningScore[] => {
  if (!c || typeof c !== "object") return [];

  return Object.entries(c)
    .filter(([key]) => /^I\d+$/i.test(key))
    .map(([key, raw]) => {
      const order = Number(key.slice(1)) || 0;
      return {
        key,
        order,
        label: String(order),
        scores: ensurePair(raw as RawPair),
      };
    })
    .sort((a, b) => a.order - b.order);
};

export const pickTotals = (
  c: Record<string, unknown> | undefined,
  keys: string[]
): Record<string, [number, number] | null> => {
  if (!c || typeof c !== "object") {
    return keys.reduce((acc, key) => {
      acc[key] = null;
      return acc;
    }, {} as Record<string, [number, number] | null>);
  }

  return keys.reduce((acc, key) => {
    if (Object.prototype.hasOwnProperty.call(c, key)) {
      acc[key] = ensurePair((c as any)[key]);
    } else {
      acc[key] = null;
    }
    return acc;
  }, {} as Record<string, [number, number] | null>);
};

export type StsMetric = {
  key: string;
  label: string;
  value: number;
};

const normalizeLabel = (raw: string): string => {
  return raw
    .replace(/[_^]/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();
};

export const parseStsMetrics = (sts: string | null | undefined): StsMetric[] => {
  if (typeof sts !== "string" || sts.trim().length === 0) return [];

  const parts = sts
    .split("|")
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0);

  const index = parts.findIndex((segment) => segment.startsWith("STS="));
  if (index === -1) return [];

  const labelsSegment = parts[index].slice(4); // remove "STS="
  const valuesSegment = parts[index + 1] ?? "";

  const labels = labelsSegment
    .split(",")
    .map((label) => normalizeLabel(label))
    .filter((label) => label.length > 0);

  const trimmedValues = valuesSegment.startsWith("^")
    ? valuesSegment.slice(1)
    : valuesSegment;

  const values = trimmedValues
    .split(",")
    .map((value) => parseNumber(value));

  const metrics: StsMetric[] = [];

  for (let i = 0; i < labels.length; i += 1) {
    const label = labels[i];
    const value = Number.isFinite(values[i]) ? values[i] : 0;
    const key = label.replace(/\s+/g, "_");

    metrics.push({ key, label, value });
  }

  return metrics;
};
